import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import DashboardLayout from "./components/layout/DashboardLayout";
import ToastProvider from "./components/ui/ToastProvider";

// Code-splitting: every page is its own chunk, fetched on first navigation.
// Vendor libs (react, lightweight-charts, lucide) live in dedicated vendor
// chunks (see vite.config.js manualChunks). Initial bundle drops from
// ~612 KB to <100 KB shell + ~140 KB react vendor.
const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Backtest = lazy(() => import("./pages/Backtest"));
const Settings = lazy(() => import("./pages/Settings"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminConfig = lazy(() => import("./pages/AdminConfig"));
const AdminAudit = lazy(() => import("./pages/AdminAudit"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ExchangeSettings = lazy(() => import("./pages/ExchangeSettings"));
const TradeHistory = lazy(() => import("./pages/TradeHistory"));
const RiskDashboard = lazy(() => import("./pages/RiskDashboard"));
const CopyTrading = lazy(() => import("./pages/CopyTrading"));
const Journal = lazy(() => import("./pages/Journal"));
const GridTrading = lazy(() => import("./pages/GridTrading"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const Webhooks = lazy(() => import("./pages/Webhooks"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const AiInsights = lazy(() => import("./pages/AiInsights"));
const Moonshot = lazy(() => import("./pages/Moonshot"));
const Portfolio = lazy(() => import("./pages/Portfolio"));

function PageFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-temto-dark">
      <div className="animate-pulse text-sm text-text-muted">Lädt …</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const token = useAuthStore((s) => s.token);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  if (!ready) return null;
  if (!token) return <Navigate to="/welcome" replace />;
  return children;
}

export default function App() {
  return (
    <ToastProvider>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/welcome" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="portfolio" element={<Portfolio />} />
            <Route path="trades" element={<TradeHistory />} />
            <Route path="backtest" element={<Backtest />} />
            <Route path="settings" element={<Settings />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="exchange" element={<ExchangeSettings />} />
            <Route path="risk" element={<RiskDashboard />} />
            <Route path="copy-trading" element={<CopyTrading />} />
            <Route path="journal" element={<Journal />} />
            <Route path="grid-trading" element={<GridTrading />} />
            <Route path="api-keys" element={<ApiKeys />} />
            <Route path="webhooks" element={<Webhooks />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="ai-insights" element={<AiInsights />} />
            <Route path="moonshot" element={<Moonshot />} />
            <Route path="admin" element={<AdminDashboard />} />
            <Route path="admin/users" element={<AdminUsers />} />
            <Route path="admin/config" element={<AdminConfig />} />
            <Route path="admin/audit" element={<AdminAudit />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ToastProvider>
  );
}
